"use strict";
/**
 * Namespaced owner references.
 *
 * WHY A REF RATHER THAN A UID
 *
 * A Firebase Auth uid answers "who made this request". It does not answer
 * "whose work is this". The two diverge the moment a tracked person has no
 * login: a credit analyst created with `hasLogin: false` has an operational
 * record and a document id, but no uid at all, so keying attribution on uid
 * makes their own work unattributable to them.
 *
 * The operational record's DOCUMENT ID is the permanent attribution identity.
 * It exists before a login does, it survives the invitation accept path (which
 * rewrites the `analystId` FIELD in place and never the document id), and it
 * therefore needs no migration when that person is later given credentials. A
 * uid is a credential attribute and never keys business data.
 *
 * SHAPE
 *
 * One flat string, deliberately not a nested object, so Firestore equality
 * filters and existing composite indexes keep working unchanged:
 *
 *   user:<uid>              a person who authenticates
 *   analyst:<analystDocId>  companies/{c}/credit_analysts/{docId}
 *   broker:<brokerDocId>    companies/{c}/brokers/{docId}
 *
 * The id segment is bounded to the Firestore document-id character set we
 * actually mint (alphanumerics, underscore, hyphen), which also guarantees the
 * separator colon appears exactly once.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.OWNER_REF_TYPES = exports.OwnerRefSchema = exports.OWNER_REF_MESSAGE = exports.OWNER_REF_REGEX = void 0;
exports.parseOwnerRef = parseOwnerRef;
exports.makeOwnerRef = makeOwnerRef;
const zod_1 = require("zod");
/** The one canonical shape. Prefix is lower case; the id carries no colon. */
exports.OWNER_REF_REGEX = /^(user|analyst|broker):[A-Za-z0-9_-]{1,120}$/;
/**
 * Rejection message. One constant rather than four copies, so the input and
 * stored-record schemas in lodgement.ts and lodgementTarget.ts cannot drift
 * into describing the format differently.
 */
exports.OWNER_REF_MESSAGE = 'ownerId must be a namespaced owner ref: user:<uid> | analyst:<docId> | broker:<docId>';
/**
 * The validator every ownerId field is built from.
 *
 * Required and regex-checked as written. A field that may be OMITTED (an input
 * schema letting the server resolve the caller's own primary ref) composes
 * `.nullish()` onto this; a field that is PERSISTED uses it bare, because
 * every stored row has a canonical owner by the time it is written.
 */
exports.OwnerRefSchema = zod_1.z.string().trim().regex(exports.OWNER_REF_REGEX, exports.OWNER_REF_MESSAGE);
/** Character set and length bound for the id segment on its own. */
const OWNER_REF_ID_REGEX = /^[A-Za-z0-9_-]{1,120}$/;
/** Every accepted prefix, for callers that need to enumerate them. */
exports.OWNER_REF_TYPES = ['user', 'analyst', 'broker'];
/**
 * Split a ref into type and id, or return null when it is not a valid ref.
 *
 * Validates against {@link OWNER_REF_REGEX} FIRST, then splits on the first
 * colon only, so a malformed value can never be coerced into a plausible
 * looking pair. Returning null rather than throwing lets read paths classify
 * unknown stored values without turning a display into an error.
 */
function parseOwnerRef(ref) {
    if (typeof ref !== 'string' || !exports.OWNER_REF_REGEX.test(ref))
        return null;
    const separator = ref.indexOf(':');
    return {
        type: ref.slice(0, separator),
        id: ref.slice(separator + 1),
    };
}
/**
 * Build a ref from a type and a raw document id or uid.
 *
 * Throws rather than returning a sentinel: a write path that cannot name its
 * own owner must fail loudly, not persist an unparseable string that only
 * surfaces as a missing row months later.
 */
function makeOwnerRef(type, id) {
    if (type !== 'user' && type !== 'analyst' && type !== 'broker') {
        throw new Error(`makeOwnerRef: unknown owner ref type "${String(type)}"`);
    }
    if (typeof id !== 'string' || !OWNER_REF_ID_REGEX.test(id)) {
        throw new Error(`makeOwnerRef: invalid ${type} id. Expected 1 to 120 characters matching [A-Za-z0-9_-]`);
    }
    return `${type}:${id}`;
}
